import React, { useRef, useEffect, useState } from 'react';
import { LiveFrame } from '../components/junctions/LiveFrame';
import { API_BASE } from '../baseapi';
import type { HoldingRoom } from './types';
import {
  Video,
  X,
  Users,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Clock
} from 'lucide-react';

interface HoldingAreaCCTVModalProps {
  camId: string;
  room: HoldingRoom;
  areaName: string;
  sector: string;
  onClose: () => void;
}

interface SeatingSpot {
  id: number;
  row: number;
  col: number;
  x: number; // normalized 0..1
  y: number; // normalized 0..1
  status: 'empty' | 'reserved' | 'occupied';
  occupantId: number | null;
}

interface Person {
  id: number;
  seatId: number;
  seatX: number;
  seatY: number;
  x: number;
  y: number;
  state: 'entering' | 'sitting_down' | 'seated' | 'standing_up' | 'exiting';
  sitProgress: number; // 0.0 (standing) to 1.0 (seated)
  sitTimer: number;
  speed: number;
  color: string;
  conf: string;
  stepPhase: number;
  breatheOffset: number;
  waypoints: { x: number; y: number }[];
  currentWp: number;
}

// Simple deterministic string hash for stable per-room randomization
function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export const HoldingAreaCCTVModal: React.FC<HoldingAreaCCTVModalProps> = ({
  camId,
  room,
  areaName,
  sector,
  onClose
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Live data (GET /api/holding answered, room.live is set): show the box's real camera, or say that it has
  // none. The animated scene below is only for the offline demo, never next to real figures.
  const realCam = /^CAM-\d+-\d+$/.test(camId) ? camId : (room.cams || []).find(c => /^CAM-\d+-\d+$/.test(c));
  const mode: 'sim' | 'live' | 'none' = room.live === undefined ? 'sim' : realCam ? 'live' : 'none';
  const [noPicture, setNoPicture] = useState(false);
  useEffect(() => { setNoPicture(false); }, [realCam]);
  const roomRef = useRef(room);
  roomRef.current = room;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;

    const colors = [
      '#F8FAFC', '#FCD34D', '#F97316', '#38BDF8',
      '#A78BFA', '#F472B6', '#34D399', '#FB7185',
      '#FDE047', '#67E8F9', '#CBD5E1', '#E2E8F0',
      '#F43F5E', '#818CF8', '#4ADE80', '#38BDF8'
    ];

    // Build 50 Seating Spots (10 columns x 5 rows)
    const spots: SeatingSpot[] = [];
    let spotCounter = 0;

    for (let r = 0; r < 5; r++) {
      for (let c = 0; c < 10; c++) {
        spots.push({
          id: ++spotCounter,
          row: r,
          col: c,
          x: 0.10 + c * 0.088,
          y: 0.44 + r * 0.108,
          status: 'empty',
          occupantId: null
        });
      }
    }

    const corridorY = 0.36;

    // Helper: build smooth path from outside left door to seat
    const buildEntryPath = (sx: number, sy: number) => {
      return [
        { x: -0.04, y: corridorY }, // Outside left door
        { x: 0.06, y: corridorY },  // Step through left door
        { x: sx, y: corridorY },    // Walk along corridor to seat column
        { x: sx, y: sy }            // Walk down column onto mat
      ];
    };

    // Helper: build smooth path from seat to outside right door
    const buildExitPath = (sx: number, sy: number) => {
      return [
        { x: sx, y: sy },          // From mat
        { x: sx, y: corridorY },   // Walk up to corridor
        { x: 0.94, y: corridorY },  // Walk along corridor to right door
        { x: 1.05, y: corridorY }   // Step through right door outside
      ];
    };

    // Initial target count based on card
    const initialTarget = Math.max(1, Math.min(50, roomRef.current.currentCount));

    // Shuffle spots deterministically based on camera/room ID
    const seed = hashCode(camId + room.id + room.name);
    const shuffledSpots = [...spots].sort((a, b) => {
      const hA = (a.id * 997 + seed) % 10007;
      const hB = (b.id * 997 + seed) % 10007;
      return hA - hB;
    });

    const chosenSpots = shuffledSpots.slice(0, initialTarget);

    let personIdCounter = 0;
    const people: Person[] = [];

    // Helper: spawn entering person heading to an empty spot
    const spawnEnteringPerson = (targetSpot: SeatingSpot) => {
      targetSpot.status = 'reserved';
      const pId = ++personIdCounter;
      targetSpot.occupantId = pId;

      const path = buildEntryPath(targetSpot.x, targetSpot.y);
      people.push({
        id: pId,
        seatId: targetSpot.id,
        seatX: targetSpot.x,
        seatY: targetSpot.y,
        x: path[0].x,
        y: path[0].y,
        state: 'entering',
        sitProgress: 0,
        sitTimer: 350 + Math.floor(Math.random() * 350), // Resting dwell
        speed: 0.0018 + (pId % 4) * 0.0003,
        color: colors[pId % colors.length],
        conf: (0.91 + (pId % 8) * 0.01).toFixed(2),
        stepPhase: 0,
        breatheOffset: (pId * 0.5) % (Math.PI * 2),
        waypoints: path,
        currentWp: 1
      });
    };

    // Initialize population with natural mix
    chosenSpots.forEach((spot, idx) => {
      const pId = ++personIdCounter;
      spot.status = 'occupied';
      spot.occupantId = pId;

      let initState: 'entering' | 'sitting_down' | 'seated' | 'standing_up' | 'exiting' = 'seated';
      let sitProg = 1.0;
      let path: { x: number; y: number }[] = [];
      let initX = spot.x;
      let initY = spot.y;
      let wp = 0;

      if (initialTarget >= 4 && idx === 0) {
        initState = 'entering';
        sitProg = 0;
        path = buildEntryPath(spot.x, spot.y);
        initX = 0.06 + Math.random() * (spot.x - 0.06);
        initY = corridorY;
        wp = 2;
        spot.status = 'reserved';
      } else if (initialTarget >= 4 && idx === 1) {
        initState = 'exiting';
        sitProg = 0;
        path = buildExitPath(spot.x, spot.y);
        initX = spot.x + (0.94 - spot.x) * (0.2 + Math.random() * 0.5);
        initY = corridorY;
        wp = 2;
        spot.status = 'empty';
        spot.occupantId = null;
      }

      people.push({
        id: pId,
        seatId: spot.id,
        seatX: spot.x,
        seatY: spot.y,
        x: initX,
        y: initY,
        state: initState,
        sitProgress: sitProg,
        sitTimer: 120 + Math.floor(Math.random() * 450) + idx * 25,
        speed: 0.0018 + (idx % 4) * 0.0003,
        color: colors[idx % colors.length],
        conf: (0.91 + (idx % 8) * 0.01).toFixed(2),
        stepPhase: Math.random() * Math.PI * 2,
        breatheOffset: (idx * 0.5) % (Math.PI * 2),
        waypoints: path,
        currentWp: wp
      });
    });

    let t = 0;
    let fanAngle = 0;
    let animId: number;

    const render = () => {
      const g = cv.getContext('2d');
      if (!g) return;

      const targetW = cv.clientWidth || 780;
      const targetH = cv.clientHeight || 420;
      if (cv.width !== targetW) cv.width = targetW;
      if (cv.height !== targetH) cv.height = targetH;
      const W = cv.width;
      const H = cv.height;

      t += 1;
      fanAngle += 0.16;

      // Real-time target count from card
      const liveTargetCount = Math.max(1, Math.min(50, roomRef.current.currentCount));

      // ==========================================
      // 1. MAINTAIN POPULATION & SMOOTH STATE TRANSITIONS
      // ==========================================
      const emptySpots = spots.filter(s => s.status === 'empty');
      const activeCount = people.length;

      // If under live target count, smoothly spawn an entrant
      if (activeCount < liveTargetCount && emptySpots.length > 0 && Math.random() < 0.04) {
        const randomSpot = emptySpots[Math.floor(Math.random() * emptySpots.length)];
        spawnEnteringPerson(randomSpot);
      }

      for (let i = people.length - 1; i >= 0; i--) {
        const p = people[i];

        if (p.state === 'entering') {
          const target = p.waypoints[p.currentWp];
          if (target) {
            const dx = target.x - p.x;
            const dy = target.y - p.y;
            const dist = Math.hypot(dx, dy);

            if (dist < 0.004) {
              p.currentWp++;
              if (p.currentWp >= p.waypoints.length) {
                // Reached seat mat smoothly! Begin sitting down
                p.x = p.seatX;
                p.y = p.seatY;
                p.state = 'sitting_down';
                p.sitProgress = 0;
              }
            } else {
              const step = Math.min(dist, p.speed);
              p.x += (dx / dist) * step;
              p.y += (dy / dist) * step;
              p.stepPhase += step * 90;
            }
          }
        } else if (p.state === 'sitting_down') {
          // Smooth transition from standing height to seated posture
          p.sitProgress += 0.05;
          if (p.sitProgress >= 1.0) {
            p.sitProgress = 1.0;
            p.state = 'seated';
            const sp = spots.find(s => s.id === p.seatId);
            if (sp) sp.status = 'occupied';
          }
        } else if (p.state === 'seated') {
          p.sitTimer--;
          if (p.sitTimer <= 0) {
            // Time to stand up!
            p.state = 'standing_up';
            const sp = spots.find(s => s.id === p.seatId);
            if (sp) {
              sp.status = 'empty';
              sp.occupantId = null;
            }
          }
        } else if (p.state === 'standing_up') {
          // Smooth transition from seated posture up to standing height
          p.sitProgress -= 0.05;
          if (p.sitProgress <= 0) {
            p.sitProgress = 0;
            p.state = 'exiting';
            p.waypoints = buildExitPath(p.seatX, p.seatY);
            p.currentWp = 1;
          }
        } else if (p.state === 'exiting') {
          const target = p.waypoints[p.currentWp];
          if (target) {
            const dx = target.x - p.x;
            const dy = target.y - p.y;
            const dist = Math.hypot(dx, dy);

            if (dist < 0.004) {
              p.currentWp++;
              if (p.currentWp >= p.waypoints.length || p.x > 1.03) {
                // Smoothly walked through the exit door and out of view
                people.splice(i, 1);
                continue;
              }
            } else {
              const step = Math.min(dist, p.speed * 1.15);
              p.x += (dx / dist) * step;
              p.y += (dy / dist) * step;
              p.stepPhase += step * 90;
            }
          }
        }
      }

      // ==========================================
      // 2. DRAW ROOM ENVIRONMENT
      // ==========================================
      g.fillStyle = '#070C12';
      g.fillRect(0, 0, W, H);

      const horizonY = H * 0.25;
      const floorH = H - horizonY;

      // Room Back Wall
      g.fillStyle = '#0D161F';
      g.fillRect(0, 0, W, horizonY);
      g.strokeStyle = '#162432';
      g.lineWidth = 1;
      g.strokeRect(0, 0, W, horizonY);

      // Overhead Roof Beams & Ceiling Fans
      g.strokeStyle = '#121F2C';
      g.lineWidth = 2;
      for (let x = 0.12; x <= 0.90; x += 0.156) {
        const vx = W * x;
        g.beginPath();
        g.moveTo(vx, 0);
        g.lineTo(vx, horizonY);
        g.stroke();

        const fanY = horizonY * 0.46;
        const fanR = 14;
        g.fillStyle = '#060E16';
        g.beginPath();
        g.arc(vx, fanY, 4, 0, Math.PI * 2);
        g.fill();

        g.strokeStyle = 'rgba(56, 189, 248, 0.45)';
        g.lineWidth = 2.2;
        for (let f = 0; f < 3; f++) {
          const fa = fanAngle + (f * Math.PI * 2) / 3;
          g.beginPath();
          g.moveTo(vx, fanY);
          g.lineTo(vx + Math.cos(fa) * fanR, fanY + Math.sin(fa) * (fanR * 0.45));
          g.stroke();
        }
      }

      // Main Floor Surface

      // Industrial Roof Truss & Overhead Lights
      g.strokeStyle = '#142735';
      g.lineWidth = 1.5;
      for (let truss = 0; truss < 7; truss++) {
        const tx = W * (0.08 + truss * 0.14);
        g.beginPath();
        g.moveTo(tx, 0);
        g.lineTo(tx, horizonY - 10);
        g.stroke();

        // Hanging Fluorescent Bay Light
        g.fillStyle = '#1B3D54';
        g.fillRect(tx - 18, horizonY - 24, 36, 6);
        g.fillStyle = 'rgba(180, 230, 255, 0.4)';
        g.fillRect(tx - 15, horizonY - 20, 30, 3);
      }

      // Hall Floor with Perspective Tile Grid
      g.fillStyle = '#09131C';
      g.fillRect(0, horizonY, W, floorH);

      // Perspective Grid Lines
      g.strokeStyle = '#122433';
      g.lineWidth = 1;
      for (let i = 0; i <= 10; i++) {
        const xAtHorizon = W * (0.05 + i * 0.09);
        const xAtBottom = W * (0.0 + i * 0.10);
        g.beginPath();
        g.moveTo(xAtHorizon, horizonY);
        g.lineTo(xAtBottom, H);
        g.stroke();
      }

      for (let j = 0; j <= 6; j++) {
        const yLine = horizonY + floorH * Math.pow(j / 6, 1.35);
        g.beginPath();
        g.moveTo(0, yLine);
        g.lineTo(W, yLine);
        g.stroke();
      }

      // Yellow Walkway Safety Lines
      g.strokeStyle = 'rgba(234, 179, 8, 0.35)';
      g.lineWidth = 2;
      g.setLineDash([8, 6]);
      g.beginPath();
      g.moveTo(W * 0.04, horizonY + 12);
      g.lineTo(W * 0.96, horizonY + 12);
      g.stroke();
      g.setLineDash([]);

      g.fillStyle = 'rgba(100, 150, 180, 0.45)';
      g.font = '8px "Inter", system-ui, sans-serif';
      g.fillText('ENTRANCE & RELEASE CORRIDOR', W * 0.5 - 75, horizonY + 16);

      // Entrance Door Portal (Left)
      g.fillStyle = 'rgba(0, 240, 181, 0.15)';
      g.fillRect(6, horizonY - 30, 120, 26);
      g.strokeStyle = '#00F0B5';
      g.lineWidth = 1.2;
      g.strokeRect(6, horizonY - 30, 120, 26);
      g.fillStyle = '#00F0B5';
      g.font = 'bold 8.5px "Inter", system-ui, sans-serif';
      g.fillText('◀ ENTRY [INFLOW]', 12, horizonY - 13);

      // Exit Door Portal (Right)
      g.fillStyle = 'rgba(245, 158, 11, 0.15)';
      g.fillRect(W - 126, horizonY - 30, 120, 26);
      g.strokeStyle = '#F59E0B';
      g.lineWidth = 1.2;
      g.strokeRect(W - 126, horizonY - 30, 120, 26);
      g.fillStyle = '#F59E0B';
      g.font = 'bold 8.5px "Inter", system-ui, sans-serif';
      g.fillText('EXIT [RELEASE] ▶', W - 120, horizonY - 13);

      // ==========================================
      // 3. DRAW 50 SEATING MATS
      // ==========================================
      spots.forEach(sp => {
        const sx = W * sp.x;
        const sy = H * sp.y;
        const matW = 34;
        const matH = 20;

        const isOcc = sp.status === 'occupied';

        g.fillStyle = isOcc ? '#132433' : '#0B151F';
        g.fillRect(sx - matW / 2, sy - matH / 2, matW, matH);
        g.strokeStyle = isOcc ? '#1A3F5C' : '#12202C';
        g.lineWidth = 0.8;
        g.strokeRect(sx - matW / 2, sy - matH / 2, matW, matH);

        g.fillStyle = isOcc ? '#355F82' : '#1D303F';
        g.font = '6.5px "Inter", system-ui, sans-serif';
        g.fillText(`#${String(sp.id).padStart(2, '0')}`, sx - 6, sy - matH / 2 + 7);
      });

      // ==========================================
      // 4. RENDER PEOPLE (SORTED BY DEPTH)
      // ==========================================
      const sortedPeople = [...people].sort((a, b) => a.y - b.y);

      sortedPeople.forEach(p => {
        const px = W * p.x;
        const py = H * p.y;
        const depth = (p.y - 0.30) / 0.65;
        const scale = 0.65 + depth * 0.55;

        const isSitting = p.sitProgress > 0.6;
        const standH = 36 * scale;
        const sitH = 22 * scale;
        const curH = standH - p.sitProgress * (standH - sitH);
        const curW = (isSitting ? 22 : 14) * scale;

        const bX = px - curW / 2;
        const bY = py - curH;
        const bW = curW;
        const bH = curH;

        // Render Realistic Devotee Silhouette
        const breathe = Math.sin(p.breatheOffset) * 0.8;
        const headR = (isSitting ? 5.5 : 4.5) * scale;
        const headY = bY + headR + breathe;

        // Body Clothing / Traditional Robes
        g.fillStyle = p.color;
        g.beginPath();
        if (isSitting) {
          g.ellipse(px, py - 7 * scale, curW * 0.46, curH * 0.42, 0, 0, Math.PI * 2);
        } else {
          g.ellipse(px, py - curH * 0.45, curW * 0.40, curH * 0.38, 0, 0, Math.PI * 2);
        }
        g.fill();

        // Head
        g.fillStyle = '#D4A373';
        g.beginPath();
        g.arc(px, headY, headR, 0, Math.PI * 2);
        g.fill();

        // AI Vision Bounding Box
        const isEntry = p.state === 'entering';
        const isExit = p.state === 'exiting';
        const boxColor = isEntry ? '#00F0B5' : isExit ? '#F59E0B' : '#38BDF8';

        g.strokeStyle = boxColor;
        g.lineWidth = 1;
        g.strokeRect(bX - 2, bY - 2, bW + 4, bH + 4);

        // Reticle Corner Accents
        const cl = 4;
        g.strokeStyle = boxColor;
        g.lineWidth = 1.6;
        g.beginPath();
        g.moveTo(bX - 2, bY - 2 + cl); g.lineTo(bX - 2, bY - 2); g.lineTo(bX - 2 + cl, bY - 2);
        g.moveTo(bX + bW + 2 - cl, bY - 2); g.lineTo(bX + bW + 2, bY - 2); g.lineTo(bX + bW + 2, bY - 2 + cl);
        g.stroke();

        // Detection Tag
        if (p.state === 'seated') {
          if (depth > 0.30 && p.seatId % 2 === 0) {
            g.fillStyle = '#00F0B5';
            g.font = '7px "Inter", system-ui, sans-serif';
            g.fillText(`seated #${p.seatId}`, px - bW / 2, bY - 4);
          }
        } else {
          g.fillStyle = boxColor;
          g.font = '7.5px "Inter", system-ui, sans-serif';
          const tag = isEntry ? `▲ in -> #${p.seatId}` : isExit ? `▼ exit [${p.conf}]` : 'standing';
          g.fillText(tag, px - bW / 2, bY - 3);
        }
      });

      // Standing Security Marshal
      const secX = W * 0.05;
      const secY = horizonY + floorH * 0.16;
      g.fillStyle = '#0F172A';
      g.fillRect(secX - 5, secY - 26, 10, 18);
      g.fillStyle = '#FACC15';
      g.fillRect(secX - 4, secY - 24, 8, 12);
      g.fillStyle = '#1E293B';
      g.beginPath();
      g.arc(secX, secY - 30, 3.5, 0, Math.PI * 2);
      g.fill();
      g.strokeStyle = '#FACC15';
      g.lineWidth = 1;
      g.strokeRect(secX - 8, secY - 36, 16, 38);
      g.fillStyle = '#FACC15';
      g.font = '7px "Inter", system-ui, sans-serif';
      g.fillText('marshal', secX - 14, secY - 40);

      // Top CCTV HUD Bar
      g.fillStyle = 'rgba(4, 10, 16, 0.92)';
      g.fillRect(0, 0, W, 26);
      g.fillStyle = '#F0F6FC';
      g.font = '600 11px "Inter", system-ui, sans-serif';
      const now = new Date();
      const ms = String(Math.floor((t * 16.6) % 1000)).padStart(3, '0');
      g.fillText(
        `${camId} · ${now.toLocaleTimeString('en-IN', { hour12: false })}.${ms} · HD AI TRACKING`,
        10,
        17
      );

      // Red Blinking Live Dot
      const blink = Math.floor(t / 30) % 2 === 0;
      g.fillStyle = blink ? '#FF4F42' : '#7F1D1D';
      g.beginPath();
      g.arc(W - 105, 13, 4, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = '#F0F6FC';
      g.font = '600 10.5px "Inter", system-ui, sans-serif';
      g.fillText('LIVE CCTV', W - 95, 17);

      // Bottom CCTV HUD Bar
      const seatedCount = people.filter(p => p.state === 'seated' || p.state === 'sitting_down').length;
      const inTransitCount = people.filter(p => p.state === 'entering').length;
      const outTransitCount = people.filter(p => p.state === 'exiting' || p.state === 'standing_up').length;

      g.fillStyle = 'rgba(4, 10, 16, 0.92)';
      g.fillRect(0, H - 26, W, 26);
      g.fillStyle = '#00F0B5';
      g.font = '600 10.5px "Inter", system-ui, sans-serif';
      g.fillText(
        `DEVOTEES: ${roomRef.current.currentCount} / ${roomRef.current.capacity}   SEATED: ${seatedCount}   ENTERING: +${inTransitCount}   EXITING: -${outTransitCount}   AI DETECTIONS: ${people.length}`,
        10,
        H - 9
      );

      animId = requestAnimationFrame(render);
    };

    render();
    return () => cancelAnimationFrame(animId);
  }, [camId, room.id]);

  const occPct = Math.round((room.currentCount / room.capacity) * 100);

  return (
    <div
      className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
      id="holdingCamModal"
      onClick={e => {
        if ((e.target as HTMLElement).id === 'holdingCamModal') onClose();
      }}
    >
      <div className="w-[840px] max-w-full bg-[var(--panel)] border border-[var(--line-bright)] rounded-[6px] shadow-2xl overflow-hidden flex flex-col transition-colors duration-200">
        {/* Header Bar */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-[var(--panel-2)] border-b border-[var(--line)]">
          <span className="font-mono font-bold text-[11px] px-2 py-0.5 rounded bg-orange-500/15 border border-orange-500/30 text-[var(--in)] flex items-center gap-1.5">
            <Video className="w-3 h-3 text-[var(--in)]" />
            {camId}
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="text-[12.5px] font-semibold text-[var(--txt)] truncate font-['Outfit',sans-serif]">
              {room.name} · {room.id}
            </h3>
            <span className="text-[9.5px] text-[var(--muted)] truncate block font-mono">
              {areaName} ({sector}) · Monitored Holding Hall
            </span>
          </div>
          <button
            className="bg-[var(--panel-elevated)] hover:bg-[var(--line)] border border-[var(--line)] text-[var(--muted)] hover:text-[var(--txt)] rounded-[3px] p-1 px-2.5 text-[10px] font-mono uppercase transition cursor-pointer flex items-center gap-1 active:scale-95"
            onClick={onClose}
          >
            <span>Close</span>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Video Canvas Area */}
        <div className="relative aspect-video bg-[#03080E] overflow-hidden flex items-center justify-center border-b border-[var(--line-soft)]">
          {mode === 'sim' && <canvas ref={canvasRef} className="w-full h-full block" />}
          {mode === 'live' && !noPicture && (
            <LiveFrame
              key={realCam}
              src={`${API_BASE}cameras/${encodeURIComponent(realCam as string)}/snapshot.jpg`}
              alt={`Camera ${realCam}`}
              className="w-full h-full object-contain bg-black"
              onError={() => setNoPicture(true)}
            />
          )}
          {(mode === 'none' || (mode === 'live' && noPicture)) && (
            <div className="text-center px-6">
              <Video className="w-8 h-8 mx-auto text-neutral-500 mb-2" />
              <div className="text-sm font-bold text-neutral-200">
                {mode === 'none' ? 'No camera in this box yet' : 'Camera is offline'}
              </div>
              <p className="text-[11px] text-neutral-400 mt-1">
                {mode === 'none'
                  ? 'Video and counts appear here once a camera is installed and mapped to this box.'
                  : `${realCam} is not sending a picture right now. It reconnects by itself when the camera is back on the network.`}
              </p>
              {mode === 'live' && (
                <button onClick={() => setNoPicture(false)} className="mt-3 px-3 py-1 rounded border border-neutral-600 text-[11px] text-neutral-200 hover:bg-neutral-800 cursor-pointer">Try again</button>
              )}
            </div>
          )}
        </div>

        {/* Telemetry Footer (the demo's descriptive tiles are hidden when live data is shown) */}
        {mode !== 'sim' && (
          <div className="grid grid-cols-3 gap-2.5 p-3 bg-[var(--panel-2)] text-[11px]">
            {[
              ['People in box now', room.live ? `${room.currentCount} / ${room.capacity}` : `— / ${room.capacity}`, room.live ? `${occPct}% of seats` : 'not measured'],
              ['Entering', room.live ? `+${room.inflowRate}/min` : '—', 'average of last 15 min'],
              ['Leaving', room.live ? `-${room.outflowRate}/min` : '—', 'average of last 15 min'],
            ].map(([label, value, note]) => (
              <div key={label} className="p-2.5 rounded-[4px] bg-[var(--panel)] border border-[var(--line-soft)] shadow-sm">
                <span className="text-[9px] text-[var(--muted)] uppercase font-semibold tracking-wider block mb-0.5">{label}</span>
                <span className="font-mono text-[16px] font-bold text-[var(--txt)] block">{value}</span>
                <span className="text-[8.5px] font-mono text-[var(--dim)]">{note}</span>
              </div>
            ))}
          </div>
        )}
        <div className={`${mode === 'sim' ? 'grid' : 'hidden'} grid-cols-2 sm:grid-cols-4 gap-2.5 p-3 bg-[var(--panel-2)] text-[11px]`}>
          <div className="p-2.5 rounded-[4px] bg-[var(--panel)] border border-[var(--line-soft)] shadow-sm">
            <span className="text-[9px] text-[var(--muted)] uppercase font-semibold tracking-wider block mb-0.5 flex items-center gap-1">
              <Users className="w-2.5 h-2.5 text-[var(--in)]" />
              Card Devotee Count
            </span>
            <span className="font-mono text-[16px] font-bold text-[var(--txt)] block">
              {room.currentCount} <span className="text-[10px] font-normal text-[var(--dim)]">/ {room.capacity}</span>
            </span>
            <span className="text-[8.5px] font-mono text-[var(--in)] font-semibold">{occPct}% Capacity</span>
          </div>

          <div className="p-2.5 rounded-[4px] bg-[var(--panel)] border border-[var(--line-soft)] shadow-sm">
            <span className="text-[9px] text-[var(--muted)] uppercase font-semibold tracking-wider block mb-0.5 flex items-center gap-1">
              <Clock className="w-2.5 h-2.5 text-sky-500" />
              Dwell Lifecycle
            </span>
            <span className="font-mono text-[15px] font-bold text-sky-600 dark:text-sky-400 block">
              Dynamic Stagger
            </span>
            <span className="text-[8.5px] font-mono text-[var(--dim)]">Continuous Flow</span>
          </div>

          <div className="p-2.5 rounded-[4px] bg-[var(--panel)] border border-[var(--line-soft)] shadow-sm">
            <span className="text-[9px] text-[var(--muted)] uppercase font-semibold tracking-wider block mb-0.5 flex items-center gap-1">
              <ArrowRight className="w-2.5 h-2.5 text-amber-500" />
              Release Gate
            </span>
            <span className="font-mono text-[15px] font-bold text-amber-600 dark:text-amber-400 block">
              Active Stream
            </span>
            <span className="text-[8.5px] font-mono text-[var(--dim)]">Darshan Corridor</span>
          </div>

          <div className="p-2.5 rounded-[4px] bg-[var(--panel)] border border-[var(--line-soft)] shadow-sm">
            <span className="text-[9px] text-[var(--muted)] uppercase font-semibold tracking-wider block mb-0.5 flex items-center gap-1">
              <ShieldCheck className="w-2.5 h-2.5 text-[var(--in)]" />
              Amenities &amp; AI
            </span>
            <div className="flex items-center gap-1.5 text-[9.5px] font-mono text-[var(--in)] mt-1 font-semibold">
              <CheckCircle2 className="w-3 h-3 text-[var(--in)]" />
              <span>Fans &amp; Coolers Active</span>
            </div>
            <span className="text-[8.5px] font-mono text-[var(--dim)]">AI Posture &amp; Density</span>
          </div>
        </div>
      </div>
    </div>
  );
};
